// Centralized ArNS service to avoid duplication
import { ARIO, ANTRegistry, ANT, ArconnectSigner } from "@ar.io/sdk/web";
import type { AntOption, PrimaryNameRequest } from '../types';

export interface ArnsData {
  currentPrimaryName: string | null;
  userAntProcessId: string | null;
  antOptions: AntOption[];
  primaryNameRequest: PrimaryNameRequest | null;
}

export interface ArnsServiceConfig {
  customProcess: any;
  cacheKey: string;
  cacheExpiryMs?: number;
}

export class ArnsService {
  private config: ArnsServiceConfig;
  private cache: Map<string, { data: ArnsData; timestamp: number }>;

  constructor(config: ArnsServiceConfig) {
    this.config = {
      ...config,
      cacheExpiryMs: config.cacheExpiryMs || 24 * 60 * 60 * 1000 // 24 hours
    };
    this.cache = new Map();
  }

  // Public method to configure the service
  configure(updates: Partial<ArnsServiceConfig>): void {
    this.config = {
      ...this.config,
      ...updates
    };
  }

  // Public method to set custom process
  setCustomProcess(customProcess: any): void {
    this.config.customProcess = customProcess;
  }

  // Cache management
  private getCachedData(walletAddress: string): ArnsData | null {
    const cacheKey = `${this.config.cacheKey}_${walletAddress}`;
    const cached = this.cache.get(cacheKey);
    
    if (!cached) return null;
    
    if (Date.now() - cached.timestamp > this.config.cacheExpiryMs!) {
      this.cache.delete(cacheKey);
      return null;
    }
    
    return cached.data;
  }

  private setCachedData(walletAddress: string, data: ArnsData): void {
    const cacheKey = `${this.config.cacheKey}_${walletAddress}`;
    this.cache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });
  }

  // Main fetch function - always gets both primary name and owned ANTs
  async fetchArnsData(walletAddress: string, forceRefresh = false): Promise<ArnsData> {
    if (!walletAddress) {
      return {
        currentPrimaryName: null,
        userAntProcessId: null,
        antOptions: [],
        primaryNameRequest: null
      };
    }

    // Check cache first
    if (!forceRefresh) {
      const cached = this.getCachedData(walletAddress);
      if (cached) {
        console.log("[ArnsService] Using cached data for:", walletAddress);
        return cached;
      }
    }

    console.log("[ArnsService] Fetching fresh ArNS data for:", walletAddress);

    try {
      // Use customProcess ONLY for ARIO operations
      const ario = ARIO.init({ process: this.config.customProcess });
      
      // Fetch primary name and owned ANTs in parallel
      const [primaryNameData, antOptions, primaryNameRequest] = await Promise.allSettled([
        this.fetchPrimaryName(ario, walletAddress),
        this.fetchOwnedAnts(walletAddress),
        this.fetchPrimaryNameRequest(ario, walletAddress)
      ]);

      const result: ArnsData = {
        currentPrimaryName: primaryNameData.status === 'fulfilled' ? primaryNameData.value?.name || null : null,
        userAntProcessId: primaryNameData.status === 'fulfilled' ? primaryNameData.value?.processId || null : null,
        antOptions: antOptions.status === 'fulfilled' ? antOptions.value : [],
        primaryNameRequest: primaryNameRequest.status === 'fulfilled' ? primaryNameRequest.value : null
      };

      // Cache the result
      this.setCachedData(walletAddress, result);
      
      console.log("[ArnsService] Fetched ArNS data:", result);
      return result;

    } catch (error) {
      console.error("[ArnsService] Error fetching ArNS data:", error);
      // Return empty data on error
      return {
        currentPrimaryName: null,
        userAntProcessId: null,
        antOptions: [],
        primaryNameRequest: null
      };
    }
  }

  // Fetch primary name for wallet
  private async fetchPrimaryName(ario: any, walletAddress: string) {
    try {
      return await ario.getPrimaryName({ address: walletAddress });
    } catch (error) {
      console.warn("[ArnsService] No primary name found for:", walletAddress);
      return null;
    }
  }

  // Fetch owned ANTs
  private async fetchOwnedAnts(walletAddress: string): Promise<AntOption[]> {
    try {
      // DO NOT use customProcess for ANTRegistry
      const registry = ANTRegistry.init();
      const antList = await registry.accessControlList({ address: walletAddress });
      
      if (!antList || (!antList.Owned?.length && !antList.Controlled?.length)) {
        return [];
      }

      const allAnts = [...(antList.Owned || []), ...(antList.Controlled || [])];
      
      const antOptions = await Promise.allSettled(
        allAnts.map(async (processId) => {
          try {
            // DO NOT use customProcess for ANT operations
            const ant = ANT.init({ processId });
            const state = await ant.getState();
            return {
              processId,
              name: state.Name || `ANT (${processId.slice(0, 6)}…)`,
            };
          } catch (error) {
            console.warn(`[ArnsService] Could not fetch ANT ${processId}:`, error);
            return {
              processId,
              name: `ANT (${processId.slice(0, 6)}…)`,
            };
          }
        })
      );

      return antOptions
        .filter(result => result.status === 'fulfilled')
        .map(result => (result as PromiseFulfilledResult<AntOption>).value);

    } catch (error) {
      console.error("[ArnsService] Error fetching owned ANTs:", error);
      return [];
    }
  }

  // Fetch pending primary name request
  private async fetchPrimaryNameRequest(ario: any, walletAddress: string): Promise<PrimaryNameRequest | null> {
    try {
      const request = await ario.getPrimaryNameRequest({ initiator: walletAddress });
      return request || null;
    } catch (error) {
      console.warn("[ArnsService] No pending primary name request for:", walletAddress);
      return null;
    }
  }

  // Request primary name
  async requestPrimaryName(params: {
    walletAddress: string;
    antName?: string;
    selectedAntProcessId?: string;
  }): Promise<{ success: boolean; message: string; txId?: string }> {
    const { walletAddress, antName, selectedAntProcessId } = params;

    if (!window.arweaveWallet) {
      throw new Error("Wallet not connected");
    }

    if (!antName && !selectedAntProcessId) {
      throw new Error("Please provide either an ArNS name or select an ANT");
    }

    try {
      // Use ArConnectSigner for ARIO operations
      const signer = new ArconnectSigner(window.arweaveWallet);
      const ario = ARIO.init({ signer, process: this.config.customProcess });

      if (selectedAntProcessId) {
        // User wants to use one of their owned ANTs
        const { id: txId } = await ario.requestPrimaryName({ 
          name: antName || selectedAntProcessId,
        });
        
        // Clear cache to force refresh
        this.cache.delete(`${this.config.cacheKey}_${walletAddress}`);
        
        return {
          success: true,
          message: `Primary name request submitted for "${antName}". Awaiting approval.`,
          txId
        };

      } else if (antName) {
        // User wants to request an existing ArNS name
        const { id: txId } = await ario.requestPrimaryName({
          name: antName,
        });
        
        // Clear cache to force refresh
        this.cache.delete(`${this.config.cacheKey}_${walletAddress}`);
        
        return {
          success: true,
          message: `Request submitted for "${antName}". Awaiting ANT owner approval.`,
          txId
        };
      }

      throw new Error("Invalid request parameters");

    } catch (error: any) {
      console.error("[ArnsService] Failed to request primary name:", error);
      return {
        success: false,
        message: error.message || "Failed to request primary name"
      };
    }
  }

  // Set undername record - FIXED version
  async setUndernameRecord(params: {
    antProcessId: string;
    undername: string;
    url?: string;
    transactionId?: string;
    ttlSeconds?: number;
  }): Promise<{ success: boolean; message: string; txId?: string }> {
    if (!window.arweaveWallet) {
      throw new Error("Wallet not connected");
    }

    const { antProcessId, undername, url, transactionId, ttlSeconds = 3600 } = params; // Default: 1 hour

    try {
      // Ensure wallet is connected
      let activeAddress: string;
      try {
        activeAddress = await window.arweaveWallet.getActiveAddress();
        console.log("[ArnsService] Active address:", activeAddress);
      } catch (permError) {
        console.log("[ArnsService] No permissions, requesting...");
        await window.arweaveWallet.connect([
          'ACCESS_ADDRESS',
          'ACCESS_PUBLIC_KEY',
          'SIGN_TRANSACTION',
          'DISPATCH'
        ]);
        activeAddress = await window.arweaveWallet.getActiveAddress();
      }

      // Use ArConnectSigner from the SDK - NOT createDataItemSigner
      const ant = ANT.init({ 
        signer: new ArconnectSigner(window.arweaveWallet), 
        processId: antProcessId
        // DO NOT pass process parameter here
      });

      // Skip ownership check - it returns wrong data
      console.log("[ArnsService] Setting undername on ANT:", antProcessId);

      // Set the record using the new setUndernameRecord method
      const recordParams: any = {
        undername,
        ttlSeconds
      };

      // Include either transaction ID or URL
      if (transactionId) {
        recordParams.transactionId = transactionId;
      } else if (url) {
        recordParams.transactionId = url;
      } else {
        throw new Error("Either URL or transaction ID is required");
      }

      console.log("[ArnsService] Setting undername with params:", recordParams);

      const { id: txId } = await ant.setUndernameRecord(recordParams);
      console.log("[ArnsService] Undername set successfully! TX ID:", txId);

      return {
        success: true,
        message: `Undername "${undername}" set successfully!`,
        txId
      };

    } catch (error: any) {
      console.error("[ArnsService] Failed to set undername:", error);
      return {
        success: false,
        message: error.message || "Failed to set undername"
      };
    }
  }

  // Get records for an ANT
  async getAntRecords(antProcessId: string): Promise<Record<string, any>> {
    try {
      // DO NOT use customProcess for ANT operations
      const ant = ANT.init({ processId: antProcessId });
      const records = await ant.getRecords();
      
      // Handle different response formats
      if (!records || typeof records !== 'object') {
        console.warn("[ArnsService] Unexpected records format:", records);
        return {};
      }
      
      // If records is an object with a nested structure, extract it
      if (records.Records && typeof records.Records === 'object') {
        return records.Records;
      }
      
      // Filter out any non-object entries
      const validRecords: Record<string, any> = {};
      for (const [key, value] of Object.entries(records)) {
        if (value && typeof value === 'object' && 'transactionId' in value) {
          validRecords[key] = value;
        }
      }
      
      return validRecords;
    } catch (error) {
      console.error("[ArnsService] Failed to fetch ANT records:", error);
      return {};
    }
  }

  // Clear cache
  clearCache(walletAddress?: string): void {
    if (walletAddress) {
      this.cache.delete(`${this.config.cacheKey}_${walletAddress}`);
    } else {
      this.cache.clear();
    }
  }
}

// Export singleton instance
export const arnsService = new ArnsService({
  customProcess: null, // Will be set when initialized
  cacheKey: 'arns_data'
});